import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { hrRequisitionApi } from "../api";
import { isExternalWorkflow } from "../types";
import { ReasonModal } from "#/components/common/ReasonModal";
import { useAuth } from "#/context/AuthProvider";
import type { HrRequisition } from "../types";

type PendingKind = "approve" | "reject" | "requestQuotation" | "quotationsReceived" | "evaluate";

const PENDING_CONFIG: Record<PendingKind, {
  title: string; label: string; placeholder: string; required: boolean; confirmLabel: string; confirmTone: "danger" | "primary";
}> = {
  approve:             { title: "Approve Requisition",   label: "Approval notes",   placeholder: "Optional notes",                            required: false, confirmLabel: "Approve",              confirmTone: "primary" },
  reject:              { title: "Reject Requisition",    label: "Rejection reason", placeholder: "Explain why this is being rejected",         required: true,  confirmLabel: "Reject",               confirmTone: "danger"  },
  requestQuotation:    { title: "Request Quotation",     label: "Procurement notes",placeholder: "Optional notes",                            required: false, confirmLabel: "Request Quotation",    confirmTone: "primary" },
  quotationsReceived:  { title: "Quotations Received",   label: "Notes",            placeholder: "Optional notes on quotations received",     required: false, confirmLabel: "Mark Received",        confirmTone: "primary" },
  evaluate:            { title: "Record Evaluation",     label: "Evaluation summary",placeholder: "Summarize the evaluation outcome",         required: false, confirmLabel: "Record Evaluation",    confirmTone: "primary" },
};

function useAsyncData<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(String(e?.message ?? e)); setLoading(false); } });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, rev]);

  return { data, loading, error, reload: () => setRev((r) => r + 1) };
}

const STATUS_CLASSES: Record<string, string> = {
  DRAFT:                "bg-slate-100 text-slate-600",
  SUBMITTED:            "bg-amber-50 text-amber-700 border border-amber-200",
  PENDING_APPROVAL:     "bg-amber-50 text-amber-700 border border-amber-200",
  APPROVED:             "bg-emerald-100 text-emerald-800",
  FULFILLED:            "bg-blue-100 text-blue-700",
  PARTIAL:              "bg-sky-100 text-sky-700",
  PURCHASED:            "bg-blue-100 text-blue-700",
  CLOSED:               "bg-slate-200 text-slate-600",
  REJECTED:             "bg-red-100 text-red-700",
  CANCELLED:            "bg-red-50 text-red-500",
  QUOTATION_REQUESTED:  "bg-purple-100 text-purple-700",
  QUOTATIONS_RECEIVED:  "bg-indigo-100 text-indigo-700",
  BUDGET_APPROVED:      "bg-teal-100 text-teal-700",
  BUDGET_REJECTED:      "bg-red-100 text-red-700",
  EVALUATED:            "bg-orange-100 text-orange-700",
};

function StatusBadge({ value }: { value?: string | null }) {
  const cls = STATUS_CLASSES[value ?? ""] ?? "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      {(value ?? "").replace(/_/g, " ")}
    </span>
  );
}

type BtnVariant = "green" | "blue" | "purple" | "red" | "orange";
const BTN_COLORS: Record<BtnVariant, string> = {
  green:  "bg-emerald-600 text-white hover:bg-emerald-700",
  blue:   "bg-blue-600 text-white hover:bg-blue-700",
  purple: "bg-purple-600 text-white hover:bg-purple-700",
  red:    "border border-red-300 text-red-600 hover:bg-red-50",
  orange: "bg-orange-500 text-white hover:bg-orange-600",
};

export default function RequisitionApprovalsPage() {
  const { currentUser, hasRole, hasAnyRole, hasPermission } = useAuth();
  const isAdmin = hasRole("SUPER_ADMIN") || hasAnyRole(["ADMIN"]);
  // Confirmed 2026-07-09 — Request Quotation / Quotations Received / Record Evaluation are all
  // gated backend-side on HR_REQUISITION_PROCUREMENT_PROCESS (Procurement Officer only). The
  // buttons used to render unconditionally for anyone viewing the inbox, so a Department Head
  // could see and click "Request Quotation" and get a correct-but-confusing 403. Gate them here
  // to match the real backend rule.
  const canProcessProcurement = isAdmin || hasPermission("HR_REQUISITION_PROCUREMENT_PROCESS");

  const [page, setPage] = useState(0);
  const [acting, setActing] = useState<number | null>(null);
  const [flash, setFlash] = useState<{ id: number; msg: string; err: boolean } | null>(null);
  const [pending, setPending] = useState<{ req: HrRequisition; kind: PendingKind } | null>(null);

  const { data, loading, error, reload } = useAsyncData(
    () => hrRequisitionApi.inbox({ page, size: 10 }),
    [page],
  );

  function showFlash(id: number, msg: string, err = false) {
    setFlash({ id, msg, err });
    setTimeout(() => setFlash(null), 4000);
  }

  async function doAction(id: number, fn: () => Promise<unknown>) {
    setActing(id);
    try {
      await fn();
      reload();
      showFlash(id, "Done.");
    } catch (e: unknown) {
      showFlash(id, String((e as { message?: string })?.message ?? e), true);
    } finally {
      setActing(null);
    }
  }

  function handleConfirmPending(notes: string) {
    if (!pending) return;
    const { req, kind } = pending;
    setPending(null);
    switch (kind) {
      case "approve":
        doAction(req.id, () => hrRequisitionApi.approve(req.id, notes || undefined));
        break;
      case "reject":
        doAction(req.id, () => hrRequisitionApi.reject(req.id, notes));
        break;
      case "requestQuotation":
        doAction(req.id, () => hrRequisitionApi.requestQuotation(req.id, notes || undefined));
        break;
      case "quotationsReceived":
        doAction(req.id, () => hrRequisitionApi.quotationsReceived(req.id, notes || undefined));
        break;
      case "evaluate":
        doAction(req.id, () => hrRequisitionApi.evaluate(req.id, notes || undefined));
        break;
    }
  }

  function getReqType(r: HrRequisition) {
    return r.requisitionType ?? r.type ?? null;
  }

  function renderActions(r: HrRequisition) {
    const busy = acting === r.id;
    const t = getReqType(r);
    const external = isExternalWorkflow(t);
    const s = r.status;

    const mkBtn = (label: string, onClick: () => void, variant: BtnVariant) => (
      <button
        key={label}
        type="button"
        onClick={onClick}
        disabled={busy}
        className={`rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${BTN_COLORS[variant]}`}
      >
        {label}
      </button>
    );

    const actions: React.ReactNode[] = [];

    // Only show Approve/Reject if we either don't know who the assigned approver is (no
    // workflow step data yet — don't hide a valid action over missing metadata) or it's
    // genuinely this user's turn. Admins always see it.
    const isMyTurn =
      isAdmin || !r.currentApproverUsername || r.currentApproverUsername === currentUser?.username;

    if (!external) {
      if ((s === "SUBMITTED" || s === "PENDING_APPROVAL") && isMyTurn) {
        actions.push(mkBtn("Approve", () => setPending({ req: r, kind: "approve" }), "green"));
        actions.push(mkBtn("Reject", () => setPending({ req: r, kind: "reject" }), "red"));
      }
    } else if (canProcessProcurement) {
      if (s === "SUBMITTED" || s === "PENDING_APPROVAL") {
        actions.push(mkBtn("Request Quotation", () => setPending({ req: r, kind: "requestQuotation" }), "purple"));
      }
      if (s === "QUOTATION_REQUESTED") {
        actions.push(mkBtn("Quotations Received", () => setPending({ req: r, kind: "quotationsReceived" }), "blue"));
      }
      if (s === "BUDGET_APPROVED") {
        actions.push(mkBtn("Record Evaluation", () => setPending({ req: r, kind: "evaluate" }), "orange"));
      }
    }

    return (
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        {actions}
        <Link
          to={`/hr/requisitions/${r.id}`}
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
        >
          View
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Requisition Inbox</h1>
      </div>

      {loading && <div className="rounded-xl border bg-white p-5 text-sm text-slate-500">Loading…</div>}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <strong>Error:</strong> {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          <div className="rounded-2xl border bg-white shadow-sm overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Serial No</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Requester</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Approval Step</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.content.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                      No requisitions pending your action.
                    </td>
                  </tr>
                )}
                {data.content.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono font-semibold text-slate-900">
                      {r.serialNo || `#${r.id}`}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {(getReqType(r) ?? "—").replace(/_/g, " ")}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{r.requesterName ?? "—"}</td>
                    <td className="px-4 py-3"><StatusBadge value={r.status} /></td>
                    <td className="px-4 py-3 text-slate-500">
                      {r.currentStepName
                        ? (
                          <>
                            <span className="font-semibold text-slate-700">{r.currentStepName}</span>
                            {r.currentApproverUsername && (
                              <span className="block text-xs text-slate-400">{r.currentApproverUsername}</span>
                            )}
                          </>
                        )
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {r.submittedAt ? new Date(r.submittedAt).toLocaleDateString("en-KE") : "—"}
                    </td>
                    <td className="px-4 py-3">{renderActions(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {flash && (
            <div className={`rounded-lg px-4 py-2 text-sm ${flash.err ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
              {flash.msg}
            </div>
          )}

          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">
              {data.totalElements.toLocaleString()} total — Page {data.number + 1} of{" "}
              {Math.max(1, data.totalPages)}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => p - 1)}
                disabled={data.first}
                className="rounded-lg border px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={data.last}
                className="rounded-lg border px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      <ReasonModal
        open={!!pending}
        title={pending ? PENDING_CONFIG[pending.kind].title : ""}
        label={pending ? PENDING_CONFIG[pending.kind].label : ""}
        placeholder={pending ? PENDING_CONFIG[pending.kind].placeholder : ""}
        required={pending ? PENDING_CONFIG[pending.kind].required : false}
        confirmLabel={pending ? PENDING_CONFIG[pending.kind].confirmLabel : "Confirm"}
        confirmTone={pending ? PENDING_CONFIG[pending.kind].confirmTone : "primary"}
        onCancel={() => setPending(null)}
        onConfirm={handleConfirmPending}
      />
    </div>
  );
}
